import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "@/lib/models/db";
import { fetchApiGet, fetchApiPut, fetchApiPost } from "./utils";
import { SuccessResult } from "../responseResults";

const IS_LOCAL = process.env["REACT_APP_IS_LOCAL"] === "true";

export const ListNotificationsValidation = z.object({
  page: z.coerce.number().min(1).default(1),
  pageSize: z.coerce.number().min(1).max(15).default(15),
  priorities: z.array(z.coerce.number().min(0).max(5)).optional(),
  types: z.array(z.string()).optional(),
  groups: z.array(z.string()).optional(),
  filter_to: z.array(z.enum(["UNREAD"])).optional(),
})

export type ListNotificationsParams = z.infer<typeof ListNotificationsValidation>;

export interface UserNotification extends BaseDbObject {
  user_id: string;

  notification_type: string;
  notification_priority: number;
  grouping?: string;

  title?: string;
  message: string;
  link?: string;

  is_read: boolean;
  dismissed_at?: Date;

  meta_data?: any;
}

export interface UserNotificationPreference extends BaseDbObject {
  user_id: string;

  disabled_types: string[];
  disabled_priorities: number[];
  disabled_groups: string[];
}

export enum NotificationType {
  System = "system",
  RiskCustomerActivities = "risk_customer_activities",
  TaskCreated = "task_created",
  TemplateReminder = "template_reminder",
  CustomerActivity = "customer_activity",
  RiskIngestCashSalesReport = "risk_ingest_cash_sales_report",
}

export enum NotificationPriority {
  Background = 1,
  Low = 2,
  Medium = 3,
  High = 4,
  Urgent = 5,
}

export function useListNotifications(validatedParams: ListNotificationsParams) {
  // Validate and parse parameters using the schema

  const queryParams = new URLSearchParams();

  // Add pagination parameters
  queryParams.append('page', validatedParams.page.toString());
  queryParams.append('pageSize', validatedParams.pageSize.toString());

  // Add array parameters - Express expects multiple params with same name for arrays
  if (validatedParams.priorities && validatedParams.priorities.length > 0) {
    queryParams.append('priorities', JSON.stringify(validatedParams.priorities));
  }

  if (validatedParams.types && validatedParams.types.length > 0) {
    queryParams.append('types', JSON.stringify(validatedParams.types));
  }

  if (validatedParams.groups && validatedParams.groups.length > 0) {
    queryParams.append('groups', JSON.stringify(validatedParams.groups));
  }

  if (validatedParams.filter_to && validatedParams.filter_to.length > 0) {
    queryParams.append('filter_to', JSON.stringify(validatedParams.filter_to));
  }

  return useQuery({
    queryKey: ["user_notifications", validatedParams],
    queryFn: () =>
      fetchApiGet<{ data: UserNotification[]; pagination?: any; total?: number }>("user/notifications", queryParams),
    retry: 1,
  });
}

export function useGetUnreadCount() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["user_notifications_unread", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<{ data: number }>("user/notifications/unread", queryParams),
    retry: 0,
    refetchInterval: IS_LOCAL !== true ? 1000 * 10 : undefined,
  });
}

export function useHasNotificationSubscription(interval?: number) {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["user_notifications_subscription", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<{ data: boolean, id?: string; }>("user/notifications/subscription", queryParams),
    retry: 1,
    refetchInterval: interval || 1000 * 30
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams();

  return async (ids: string[]) => {
    if (!ids.length) { return new SuccessResult("", {}); }

    const result = await fetchApiPut<{}>(
      `user/notifications/read`,
      { ids },
      queryParams,
    );

    if (result.success) {
      // queryClient.invalidateQueries({ queryKey: ["user_notifications_unread"] });
      // queryClient.invalidateQueries({ queryKey: ["user_notifications"] });
    }

    return result;
  };
}

export interface NotificationSubscription {
  notification_type: NotificationType;
  name: string;
  description: string;
  is_subscribed: boolean;
  disable_banner_alert: boolean;
}

export function useListSubscriptions() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["notification_subscriptions"],
    queryFn: () =>
      fetchApiGet<{ data: NotificationSubscription[] }>("user/notifications/types/subscriptions", queryParams),
    retry: 1,
  });
}

export function useSubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ notification_type, disable_banner_alert }: { notification_type: NotificationType; disable_banner_alert?: boolean }) => {
      const queryParams = new URLSearchParams();
      return fetchApiPost<{}>(
        "user/notifications/types/subscribe",
        { notification_type, disable_banner_alert: disable_banner_alert || false },
        queryParams,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification_subscriptions"] });
    },
  });
}

export function useUnsubscribe() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ notification_type }: { notification_type: NotificationType }) => {
      const queryParams = new URLSearchParams();
      return fetchApiPost<{}>(
        "user/notifications/types/unsubscribe",
        { notification_type },
        queryParams,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification_subscriptions"] });
    },
  });
}

export function useTestNotification() {
  return useMutation({
    mutationFn: async ({ requireInteraction }: { requireInteraction?: boolean }) => {
      const queryParams = new URLSearchParams();
      return fetchApiPost<{}>(
        "user/notifications/types/test",
        { requireInteraction: requireInteraction || false },
        queryParams,
      );
    },
  });
}
